/* eslint-disable @typescript-eslint/no-unused-vars */

import { Form } from 'antd';

import {
	coreActions,
	DataSecurityMonitor,
	MonitorFormTypes,
	PatchDataSecurityMonitorByIdRequest
} from '@sonar-software/sonar-core';
import { MenuOptions, MonitorMenu, monitorMenuConfigs } from './MonitorMenu';
import React, { forwardRef, useCallback, useImperativeHandle } from 'react';
import { useAction, UseFormInstance } from '@hooks';

export interface MonitorFormProps {
	defaultMonitor?: MonitorFormTypes;
	editing?: boolean;
	form: UseFormInstance<MonitorFormTypes>;
	hideApplications?: boolean;
	menuOptions?: MenuOptions;
	monitor?: MonitorFormTypes;
	onChange?: (monitor?: MonitorFormTypes) => void;
	showRecommendedActionTemplates?: boolean;
}

export interface MonitorFormRef {
	delete: () => void;
	save: () => void;
	saveTemplate: (overwrite: boolean) => void;
	isValid: () => boolean;
}

export const MonitorForm = forwardRef<MonitorFormRef, MonitorFormProps>(({
	defaultMonitor,
	editing,
	form,
	hideApplications = false,
	menuOptions,
	monitor,
	onChange,
	showRecommendedActionTemplates = false
}, ref) => {
	const patchMonitor = useAction(coreActions.requestPatchCoreDataSecurityMonitorsById);
	const postMonitor = useAction(coreActions.requestPostCoreDataSecurityMonitors);
	const deleteMonitor = useAction(coreActions.requestDeleteCoreDataSecurityMonitorsById);
	const postTemplate = useAction(coreActions.requestPostCoreDataSecurityActionTemplate);

	const handleValidate = useCallback((): boolean => {
		form.validate();

		const currentMonitor = form.getFieldsValue(true);

		if (!currentMonitor || !currentMonitor.monitorConfig?.monitorTypeId) {
			return false;
		}

		const appRequired = monitorMenuConfigs
			.find((config) => config.type === currentMonitor.monitorConfig?.monitorTypeId)?.applicationRequired;

		if (!currentMonitor.templateId) {
			return form.isValid && appRequired ?
				Boolean(currentMonitor.name) && Boolean(currentMonitor.applicationId) :
				Boolean(currentMonitor.name);
		}

		return form.isValid && appRequired ?
			currentMonitor.applicationIds && currentMonitor.applicationIds.length > 0 :
			true;

	}, [ form ]);

	const saveTemplate = useCallback((overwrite: boolean) => {
		const {
			applicationIds,
			monitorConfig,
			stakeholderIds,
			templateId
		} = form.getFieldsValue(true);

		postTemplate({
			applicationIds,
			monitorConfig: monitorConfig as DataSecurityMonitor['monitorConfig'],
			monitorTypeId: monitorConfig.monitorTypeId,
			overwrite: overwrite,
			stakeholderIds,
			templateId
		});
	}, [ form, postTemplate ]);

	const saveMonitor = useCallback(() => {
		const {
			applicationId,
			monitorConfig,
			name,
			notificationSchedule,
			stakeholderIds
		} = form.getFieldsValue(true);

		if (monitor?.id) {
			const updateMonitor: PatchDataSecurityMonitorByIdRequest = {
				id: monitor.id,
				monitorConfig,
				name,
				notificationSchedule,
				stakeholderIds
			};

			patchMonitor(updateMonitor);

		} else if (monitorConfig?.monitorTypeId && name) {
			postMonitor({
				applicationId,
				monitorConfig: monitorConfig as DataSecurityMonitor['monitorConfig'],
				monitorTypeId: monitorConfig.monitorTypeId,
				name,
				notificationSchedule,
				stakeholderIds
			});
		}

		form.reset();
	}, [ form, monitor, patchMonitor, postMonitor ]);

	const handleDelete = useCallback(() => {
		if (monitor?.id) {
			deleteMonitor({ monitorId: monitor?.id });
		}
	}, [ deleteMonitor, monitor ]);

	const handleOnChange = useCallback((newMonitor: MonitorFormTypes) => {
		form.setFieldsValue(newMonitor);

		if (onChange) {
			onChange(newMonitor);
		}

		handleValidate();
	}, [ form, handleValidate, onChange ]);

	const handleValuesChange = useCallback(() => {

		handleValidate();

		if (onChange) {
			onChange();
		}

	}, [ handleValidate, onChange ]);

	useImperativeHandle(ref, () => ({
		delete: () => handleDelete(),
		isValid: () => handleValidate(),
		save: () => saveMonitor(),
		saveTemplate: (overwrite: boolean) => saveTemplate(overwrite)
	}));

	return (
		<Form
			form={ form }
			layout='vertical'
			name={ `monitorForm-${monitor?.id ?? 'new'}` }
			onValuesChange={ handleValuesChange }>
			<MonitorMenu
				editing={ editing }
				fallbackMonitor={ defaultMonitor }
				hideApplications={ hideApplications }
				menuOptions={ menuOptions }
				monitor={ monitor }
				onChange={ handleOnChange }
				showRecommendedActionTemplates={ showRecommendedActionTemplates } />
		</Form>
	);
});
